/**
 * The sounds a phone makes before a conversation starts.
 *
 * Two of them, and they are not the same sound at different times:
 *
 *   ring      an inbound call is waiting for the rep. An ALERT — it should reach somebody who
 *             is looking at a CRM tab, not at the dock.
 *   ringback  the rep's own outbound call is ringing out. Not an alert: it is the sound the
 *             far end's phone is making, standing in for audio the switch has not sent.
 *
 * **They are deliberately different sounds.** A setter with two lines up has to be able to tell
 * "somebody is calling me" from "my call is ringing out" without looking away from what they are
 * doing, and two cadences of the same dual tone are not distinguishable in a noisy room.
 * Ringback is the real North American precise ringback (440 Hz + 480 Hz, 2 s on / 4 s off, ITU-T
 * E.180), which also means the rep cannot tell our local tone from a carrier's real one when
 * early media does arrive. Ring is a short chime — a rising three-note figure struck twice on a
 * bell-like voice — which no PSTN leg ever sends.
 *
 * Synthesised rather than shipped as an asset: a few oscillators and gain envelopes are a few
 * lines, and an audio file is a network fetch that can fail at exactly the moment it is needed.
 * The chime's voice is what makes it a ring and not a beep: a struck note has a fast attack and
 * a long decay, and a bell's brightness is its overtones, so each note is three sines at 1×, 2×
 * and 3× the pitch (the harmonic series a struck bar gives), each fading on its own. The first
 * version of this ring was two flat sines alternating, which reads as an organ or an alarm, and
 * was replaced on request (2026-09-16).
 *
 * A third sound is not a tone that goes on until something stops it but one note, once:
 *
 *   ding      the phone has picked a call up by itself (this browser's auto-answer, or the
 *             campaign's). Such a call does not ring, since it is answered within a moment, and
 *             without a sound the first a rep knew of it was a stranger's voice in their ear.
 *             One struck note as the phone answers, over before the call's own audio is through.
 *
 * **Nothing here decides WHEN to play.** That is `softphone-context.tsx`, which knows about
 * calls; this module knows about sound. The one rule it does enforce is that only one tone
 * sounds at a time — `play()` replaces whatever was going — because the two overlapping is worse
 * than either, and the alternative is every caller remembering to stop the other one first. The
 * ding is apart from that rule: it is over in half a second, and a ring or a ringback stopping or
 * starting beside it must not cut it off.
 */

export type Tone = "ring" | "ringback";

interface Burst {
  /** Seconds from the start of the pattern. */
  at: number;
  /** Seconds this burst lasts: a held tone's length, or a struck note's decay. */
  for: number;
  /** The frequencies sounded together. */
  hz: number[];
}

interface Pattern {
  /** Seconds before the whole thing repeats. */
  period: number;
  /** Peak gain. Ring is the louder of the two: it has to carry across a room. */
  gain: number;
  /**
   * `held`: a steady tone with a short fade at each edge, which is what a network tone is.
   * `struck`: a bell-like note — instant attack, a long exponential decay, and overtones at
   * twice and three times the pitch, quieter and shorter than the fundamental.
   */
  voice: "held" | "struck";
  bursts: Burst[];
}

/** A note, in hertz. Equal temperament, A4 = 440. */
const G5 = 783.99;
const B5 = 987.77;
const D6 = 1174.66;
const E6 = 1318.51;

/** The ding: one struck note, a step above where the ring's figure ends, so it is not taken for
 *  the start of a ring. A little quieter than the ring: it tells, it does not call. */
const DING: Pattern = {
  period: 0,
  gain: 0.15,
  voice: "struck",
  bursts: [{ at: 0, for: 0.5, hz: [E6] }],
};

const PATTERNS: Record<Tone, Pattern> = {
  // A rising three-note figure (a G major arpeggio), struck twice, then quiet for the rest of
  // the period. Bright enough to carry across a room, short enough not to nag, and nothing on
  // the PSTN sounds like it — it can only mean "a call is waiting for you".
  ring: {
    period: 3.6,
    gain: 0.18,
    voice: "struck",
    bursts: [
      { at: 0, for: 0.9, hz: [G5] },
      { at: 0.16, for: 0.9, hz: [B5] },
      { at: 0.32, for: 1.2, hz: [D6] },
      { at: 1.15, for: 0.9, hz: [G5] },
      { at: 1.31, for: 0.9, hz: [B5] },
      { at: 1.47, for: 1.4, hz: [D6] },
    ],
  },
  // North American precise ringback, as the far end's carrier would send it.
  ringback: {
    period: 6,
    gain: 0.07,
    voice: "held",
    bursts: [{ at: 0, for: 2, hz: [440, 480] }],
  },
};

/** The overtones of a struck note: multiples of the pitch, and how loud and long each is
 *  relative to the fundamental. A bar or a bell decays from the top down, so the higher
 *  partials are both quieter and shorter, which is what makes the note ring rather than buzz. */
const PARTIALS: readonly { ratio: number; gain: number; decay: number }[] = [
  { ratio: 1, gain: 1, decay: 1 },
  { ratio: 2, gain: 0.32, decay: 0.55 },
  { ratio: 3, gain: 0.12, decay: 0.35 },
];
/** Seconds from silence to the peak of a struck note: a hammer, not a swell. */
const STRIKE = 0.006;

/** The smallest gain `exponentialRampToValueAtTime` accepts — it refuses exactly 0. */
const SILENT = 0.0001;
/** Fade in and out of every burst. Square-edged tones click, and a click is what cheap sounds. */
const EDGE = 0.02;

type AudioContextCtor = typeof AudioContext;

function audioContextCtor(): AudioContextCtor | null {
  if (typeof window === "undefined") return null;
  const w = window as unknown as { webkitAudioContext?: AudioContextCtor };
  return window.AudioContext ?? w.webkitAudioContext ?? null;
}

/**
 * One AudioContext, one tone at a time.
 *
 * A class rather than module state so the provider owns its lifetime and a test can make one
 * without a browser — the same shape as `RemoteAudio` next door, and for the same reason: the
 * thing that has to be torn down on unmount should be an object somebody holds.
 */
export class Tones {
  private ctx: AudioContext | null = null;
  private timer: ReturnType<typeof setInterval> | null = null;
  private nodes: AudioNode[] = [];
  /** The ding's own nodes, kept apart so that `stop()` does not cut a ding short. */
  private dingNodes: AudioNode[] = [];
  private playing: Tone | null = null;
  /** The speaker the rep picked in the setup dialog, or null for the system default. */
  private sink: string | null = null;

  /** Which tone is sounding, or null. Exposed so a caller can avoid restarting the same one. */
  get current(): Tone | null {
    return this.playing;
  }

  /**
   * Sound `tone`, or silence for `null`.
   *
   * Takes the nullable type `toneFor` returns so the caller is one line and cannot forget the
   * stop — every way a tone should end arrives as a different answer from the same function.
   * Idempotent: called with what is already playing it does nothing, which is what makes it safe
   * from a React effect that runs on every render.
   *
   * Never throws and never rejects: a browser with no Web Audio, or one that refuses to resume a
   * suspended context, means a silent phone — which is a degraded phone, not a broken one, and
   * must not take a call down with it.
   */
  play(tone: Tone | null): void {
    if (this.playing === tone) return;
    this.stop();
    if (tone === null) return;
    const ctx = this.context();
    if (!ctx) return;
    this.playing = tone;
    const pattern = PATTERNS[tone];
    // Once immediately — a tone that started on the next tick would be a whole cadence late,
    // and the first ring is the one that matters.
    this.burst(ctx, pattern);
    this.timer = setInterval(() => this.burst(ctx, pattern), pattern.period * 1000);
  }

  /**
   * One ding, now: the phone has answered a call by itself. It does not touch the tone that is
   * playing, and is not touched by it. Like `play`, it never throws: no Web Audio means no ding.
   */
  ding(): void {
    const ctx = this.context();
    if (!ctx) return;
    // The last ding has rung out long before the next call; its nodes are let go here.
    this.release(this.dingNodes);
    this.dingNodes = [];
    for (const burst of DING.bursts) {
      this.strike(ctx, DING, burst, ctx.currentTime + burst.at, this.dingNodes);
    }
  }

  /** Silence. Safe to call when nothing is playing, and safe to call twice. */
  stop(): void {
    if (this.timer !== null) {
      clearInterval(this.timer);
      this.timer = null;
    }
    this.playing = null;
    this.release();
  }

  /** Give the audio hardware back. The context is closed, so a new one is made on the next play. */
  dispose(): void {
    this.stop();
    this.release(this.dingNodes);
    this.dingNodes = [];
    const ctx = this.ctx;
    this.ctx = null;
    if (ctx) void ctx.close().catch(() => undefined);
  }

  /**
   * Ring through a chosen speaker (`softphone/audio-devices.ts`).
   *
   * A rep who routes their calls to a headset means the ring as well: an inbound call that rang
   * out of the laptop while the call itself played into the headset is how a shared office gets
   * every rep looking up at every call. Best effort, though, and more so than the call audio —
   * `AudioContext.setSinkId` is recent Chromium only, so on everything else the ring keeps coming
   * out of the system default. Being heard is the point; which speaker is a preference.
   */
  setSink(deviceId: string | null): void {
    this.sink = deviceId;
    if (this.ctx) this.applySink(this.ctx);
  }

  private applySink(ctx: AudioContext): void {
    const withSink = ctx as AudioContext & { setSinkId?: (id: string) => Promise<void> };
    if (typeof withSink.setSinkId !== "function") return;
    try {
      // "" is the browser's word for the default device.
      void withSink.setSinkId(this.sink ?? "").catch(() => undefined);
    } catch {
      /* a browser that has the method but refuses the id — the ring stays on the default */
    }
  }

  private context(): AudioContext | null {
    if (!this.ctx) {
      const Ctor = audioContextCtor();
      if (!Ctor) return null;
      try {
        this.ctx = new Ctor();
      } catch {
        return null;
      }
      this.applySink(this.ctx);
    }
    // Autoplay policy suspends a context created before the page has been interacted with. By
    // the time any call exists the rep has clicked Connect on the dock, so this normally
    // resolves — and when it does not, the phone is silent rather than broken.
    if (this.ctx.state === "suspended") void this.ctx.resume().catch(() => undefined);
    return this.ctx;
  }

  /** Schedule one pass of the pattern, from now. */
  private burst(ctx: AudioContext, pattern: Pattern): void {
    // Everything from the previous pass has run to its `stop()` by now, but the nodes are still
    // in the list; dropping them here is what keeps a phone that rings for two minutes from
    // holding two hundred oscillators.
    this.release();
    const t0 = ctx.currentTime;
    for (const burst of pattern.bursts) {
      const start = t0 + burst.at;
      if (pattern.voice === "struck") this.strike(ctx, pattern, burst, start);
      else this.hold(ctx, pattern, burst, start);
    }
  }

  /** A steady tone: fade in, hold, fade out. Square-edged tones click, and a click is what cheap sounds. */
  private hold(ctx: AudioContext, pattern: Pattern, burst: Burst, start: number): void {
    const end = start + burst.for;
    const gain = ctx.createGain();
    gain.connect(ctx.destination);
    gain.gain.setValueAtTime(SILENT, start);
    gain.gain.exponentialRampToValueAtTime(pattern.gain, start + EDGE);
    gain.gain.setValueAtTime(pattern.gain, end - EDGE);
    gain.gain.exponentialRampToValueAtTime(SILENT, end);
    this.nodes.push(gain);
    for (const hz of burst.hz) this.oscillator(ctx, hz, gain, start, end + EDGE);
  }

  /** A struck note: each partial rises in a few milliseconds and decays on its own curve.
   *  `kept` is the list its nodes go on: the tone's, or the ding's own. */
  private strike(
    ctx: AudioContext, pattern: Pattern, burst: Burst, start: number, kept: AudioNode[] = this.nodes,
  ): void {
    for (const hz of burst.hz) {
      for (const partial of PARTIALS) {
        const end = start + burst.for * partial.decay;
        const gain = ctx.createGain();
        gain.connect(ctx.destination);
        gain.gain.setValueAtTime(SILENT, start);
        gain.gain.exponentialRampToValueAtTime(pattern.gain * partial.gain, start + STRIKE);
        gain.gain.exponentialRampToValueAtTime(SILENT, end);
        kept.push(gain);
        this.oscillator(ctx, hz * partial.ratio, gain, start, end + EDGE, kept);
      }
    }
  }

  private oscillator(
    ctx: AudioContext, hz: number, into: GainNode, start: number, stop: number,
    kept: AudioNode[] = this.nodes,
  ): void {
    const osc = ctx.createOscillator();
    osc.type = "sine";
    osc.frequency.value = hz;
    osc.connect(into);
    osc.start(start);
    osc.stop(stop);
    kept.push(osc);
  }

  private release(nodes: AudioNode[] = this.nodes): void {
    for (const node of nodes) {
      // An oscillator that has not started yet still has to be stopped, or it sounds after the
      // rep has answered — which is how a ringtone ends up playing into a live call.
      if ("stop" in node) {
        try {
          (node as OscillatorNode).stop();
        } catch {
          /* already stopped */
        }
      }
      node.disconnect();
    }
    if (nodes === this.nodes) this.nodes = [];
  }
}

/**
 * Which tone this phone should be making, given every call it has.
 *
 * A pure function of the call list so the rules are readable in one place and testable without
 * Web Audio, React or a switch. Four of them, and each exists because of a specific way the
 * obvious version is wrong:
 *
 * 1. **An inbound call waiting outranks an outbound one ringing out.** A rep can be in both
 *    states at once; the one that needs a decision from them is the one to make a noise about.
 *
 * 2. **A call the browser is about to auto-answer does not ring.** Auto-answer picks up within
 *    milliseconds, and a phone that chirps for a fraction of a second on every single call is
 *    the kind of thing a floor turns the sound off over. The predicate is `shouldAutoAnswer`
 *    itself, not "auto-answer is on", because it refuses over a live call — a second call
 *    arriving while the rep is talking is a real decision and does ring. The one exception is a
 *    supervisor's listen-in, which never rings, over a live call or not: they asked for it on
 *    the floor a moment ago, and the client asked for no ring and no ding on it (2026-09-25).
 *
 * 3. **Ringback stops the moment the far end sends any audio.** Early media is a carrier's own
 *    ringback, an IVR, or "the number you have dialled is not in service" — and playing our tone
 *    over the third one means the rep waits out an announcement that already told them why the
 *    call will not connect.
 *
 * 4. **A held call is not ringing.** `trying`/`ringing` are the states before a leg connects;
 *    hold happens after. Matching on state alone would be enough today, and stating it is what
 *    keeps a future hold-while-ringing from silently starting a ringback.
 */
export interface ToneCall {
  id: string;
  direction: "inbound" | "outbound";
  state: string;
}

export function toneFor(
  calls: readonly ToneCall[],
  options: {
    /** Calls the far end has already sent media for — see rule 3. */
    withMedia: ReadonlySet<string>;
    /** Rule 2: whether this specific inbound call is about to be answered by the browser. */
    autoAnswering: (callId: string) => boolean;
  },
): Tone | null {
  const waiting = calls.find(
    (c) => c.direction === "inbound" && c.state === "ringing" && !options.autoAnswering(c.id),
  );
  if (waiting) return "ring";
  const ringingOut = calls.find(
    (c) =>
      c.direction === "outbound" &&
      (c.state === "trying" || c.state === "ringing") &&
      !options.withMedia.has(c.id),
  );
  return ringingOut ? "ringback" : null;
}

/** Where the phone once kept a per-browser "Ring for calls" switch. */
const RETIRED_RINGER_KEY = "acl.softphone.ringer";

/**
 * The phone always rings. There is no switch.
 *
 * There was one, per browser like auto-answer next door, for the rep with the console open on a
 * laptop in a meeting. On a call floor it was the wrong trade (2026-09-24): a switch left off on
 * one computer is a phone that shows an incoming call and makes no sound, and to the rep that is
 * "my phone is not ringing", with nothing in the console to say why. A rep who wants quiet goes
 * on a break, which also stops calls being offered to them. So the stored value is never read
 * again — a browser where it was switched off must not stay silent — and this clears it away.
 */
export function forgetRingerSetting(): void {
  try {
    localStorage.removeItem(RETIRED_RINGER_KEY);
  } catch {
    /* nothing to forget, or no storage to forget it from */
  }
}
